import {
  CORE_SCHEMA_VERSION,
  type Action,
  type AutomationReceipt,
  type Capture,
  type CaptureInput,
  type ContextRelation,
  type ContextRecord,
  type CoreReference,
  type ReviewItem,
} from '../types/core'
import {
  applyCaptureAutomation,
  applyReviewResolution,
  coreRepositories,
  type CaptureAutomationWrite,
} from './coreDataService'
import {
  annotateModelFallback,
  buildModelCaptureAutomation,
  coreModelInterpretationService,
  shouldEscalateCaptureInterpretation,
  type ModelFallbackCode,
} from './coreModelInterpretationService'
import { attachmentStorageService } from './attachmentStorageService'
import type { NormalizedImage } from './imageProcessingService'


export type CaptureEntryMethod = 'keyboard' | 'dictation' | 'paste' | 'external'

const MAX_CAPTURE_LINK_LENGTH = 2_048

export const normalizeCaptureLink = (rawUrl: string): string => {
  const trimmed = rawUrl.trim()
  if (!trimmed || trimmed.length > MAX_CAPTURE_LINK_LENGTH) {
    throw new Error('Capture link is invalid.')
  }

  const candidate = /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`
  let parsed: URL
  try {
    parsed = new URL(candidate)
  } catch {
    throw new Error('Capture link is invalid.')
  }

  if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname || parsed.username || parsed.password) {
    throw new Error('Capture link is invalid.')
  }
  return parsed.toString()
}

export type CoreTodayData = {
  captures: Capture[]
  contextRecords: ContextRecord[]
  contextRelations: ContextRelation[]
  actions: Action[]
  reviewItems: ReviewItem[]
  receipts: AutomationReceipt[]
}

export type CoreCaptureOutcome = {
  capture: Capture
  contextRecord?: ContextRecord
  contextRecords: ContextRecord[]
  contextRelations: ContextRelation[]
  action?: Action
  reviewItem?: ReviewItem
  receipt: AutomationReceipt
  organizationState: 'organized' | 'review' | 'failed'
}

const ACTION_PREFIX = /^(?:todo|task|remind me(?: to)?|remember to|i need to|need to|i should|should)\s*[:—-]?\s*/i
const ACTION_VERB = /^(?:call|email|message|text|buy|book|schedule|send|finish|pick up|pay|renew|cancel|check|make|write|review|submit|order|return|clean|fix|update)\b/i
const UNCERTAIN_LANGUAGE = /\b(?:maybe|might|could|possibly|not sure|thinking about|considering|someday)\b/i
const UNCERTAIN_PREFIX = /^(?:maybe|possibly|could|might)\s+/i
const PREFERENCE_LANGUAGE = /\b(?:i prefer|i like|i love|favorite|favourite|works best for me)\b/i
const PERSONAL_STATE_LANGUAGE = /\b(?:i feel|i am feeling|feeling|i'm)\s+(?:tired|drained|stressed|anxious|overwhelmed|frustrated|low|calm|focused|energized|excited|content)\b/i
const DECISION_LANGUAGE = /\b(?:i decided|decision:|i'm going to|i am going to)\b/i
const PROCESSOR = 'core-capture-rules'
const PROCESSING_VERSION = '2'

const SUBJECT_RULES: Array<{
  key: string
  pattern: RegExp
  title: string
  kind: ContextRecord['kind']
  relation: ContextRelation['relation']
}> = [
  { key: 'boss', pattern: /\bmy boss\b/i, title: 'My boss', kind: 'person', relation: 'involves' },
  { key: 'job', pattern: /\b(?:my job|at work|workplace)\b/i, title: 'My job', kind: 'organization', relation: 'about' },
  { key: 'mom', pattern: /\bmy (?:mom|mother)\b/i, title: 'My mom', kind: 'person', relation: 'involves' },
  { key: 'dad', pattern: /\bmy (?:dad|father)\b/i, title: 'My dad', kind: 'person', relation: 'involves' },
  { key: 'partner', pattern: /\bmy (?:partner|spouse|wife|husband)\b/i, title: 'My partner', kind: 'person', relation: 'involves' },
]

let fallbackIdCounter = 0

const createId = (prefix: string) => {
  const uuid = globalThis.crypto?.randomUUID?.()
  if (uuid) return `${prefix}-${uuid}`

  fallbackIdCounter += 1
  return `${prefix}-${Date.now().toString(36)}-${fallbackIdCounter.toString(36)}`
}
const clip = (value: string, length = 110) => value.length > length ? `${value.slice(0, length - 1).trimEnd()}…` : value
const captureRef = (capture: Capture): CoreReference => ({ kind: 'capture', id: capture.id })

const inputText = (capture: Capture) => capture.inputs
  .map((input) => {
    switch (input.kind) {
      case 'text': return input.content
      case 'voice': return input.transcript ?? input.storagePath
      case 'image': return input.altText ?? input.storagePath
      case 'link': return input.title ?? input.url
      case 'file': return input.name
      case 'location': return input.label ?? `${input.latitude}, ${input.longitude}`
    }
  })
  .find((value) => value.trim())

const captureText = (capture: Capture) => capture.primaryText?.trim() || inputText(capture) || capture.id

const modelFailureCode = (error: unknown): ModelFallbackCode => {
  const name = error instanceof Error ? error.name : ''
  const message = error instanceof Error ? error.message.toLocaleLowerCase() : ''
  if (name === 'TimeoutError' || message.includes('timed out')) return 'timeout'
  if (name === 'ZodError') return 'response_contract'
  if (
    message.includes('unavailable')
    || message.includes('usage limit')
    || message.includes('authentication')
  ) return 'service'
  return 'unknown'
}

const attemptModelInterpretation = async (
  capture: Capture,
  deterministic: CaptureAutomationWrite,
  existingContextRecords: ContextRecord[],
): Promise<CaptureAutomationWrite> => {
  let proposal
  try {
    proposal = (await coreModelInterpretationService.interpretCapture(capture.id)).proposal
  } catch (error) {
    return annotateModelFallback(deterministic, 'unavailable', modelFailureCode(error))
  }

  try {
    const modelAutomation = buildModelCaptureAutomation(
      capture,
      deterministic,
      proposal,
      existingContextRecords,
    )
    return modelAutomation === deterministic
      ? annotateModelFallback(deterministic, 'no_change')
      : modelAutomation
  } catch {
    return annotateModelFallback(deterministic, 'rejected', 'proposal_validation')
  }
}

const normalizeActionTitle = (text: string) => {
  const normalized = text.replace(ACTION_PREFIX, '').replace(/[.!]+$/, '').trim()
  return clip(normalized || text.trim())
}

const looksLikeAction = (text: string) => {
  const trimmed = text.trim()
  return ACTION_PREFIX.test(trimmed)
    || ACTION_VERB.test(trimmed)
    || (UNCERTAIN_LANGUAGE.test(trimmed) && ACTION_VERB.test(trimmed.replace(UNCERTAIN_PREFIX, '')))
}

const contextKindFor = (text: string): ContextRecord['kind'] => {
  if (PREFERENCE_LANGUAGE.test(text)) return 'preference'
  if (PERSONAL_STATE_LANGUAGE.test(text)) return 'personal_state'
  if (DECISION_LANGUAGE.test(text)) return 'decision'
  return 'episode'
}

const contextFormFor = (kind: ContextRecord['kind']): ContextRecord['form'] => {
  if (kind === 'preference' || kind === 'reference') return 'reference'
  return 'observation'
}

const derivationFor = (sourceRef: CoreReference, confidence: number) => ({
  method: 'rules' as const,
  processor: PROCESSOR,
  version: PROCESSING_VERSION,
  confidence,
  sourceRefs: [sourceRef],
})

const buildContextRecord = (
  capture: Capture,
  now: string,
  id = `context-${capture.id}`,
  confirmation: ContextRecord['confirmation'] = 'inferred',
): ContextRecord => {
  const sourceRef = captureRef(capture)
  const text = captureText(capture)
  const kind = contextKindFor(text)
  const form = contextFormFor(kind)

  return {
    schemaVersion: CORE_SCHEMA_VERSION,
    id,
    userId: capture.userId,
    form,
    kind,
    status: 'active',
    confirmation,
    title: clip(text),
    summary: text,
    facts: [],
    ...(form === 'observation' ? { observation: { observedAt: capture.createdAt } } : {}),
    memberRefs: [],
    sourceRefs: [sourceRef],
    relatedRefs: [],
    derivation: derivationFor(sourceRef, confirmation === 'confirmed' ? 1 : 0.86),
    createdAt: now,
    updatedAt: now,
  }
}

const sameRef = (left: CoreReference, right: CoreReference) => left.kind === right.kind && left.id === right.id

const appendUniqueRef = (references: CoreReference[], reference: CoreReference) => (
  references.some((entry) => sameRef(entry, reference)) ? references : [...references, reference]
)

const buildSubjectContext = (
  capture: Capture,
  primaryContext: ContextRecord,
  existingRecords: ContextRecord[],
  now: string,
): { profiles: ContextRecord[]; relations: ContextRelation[]; existingProfileIds: Set<string> } => {
  const sourceRef = captureRef(capture)
  const text = captureText(capture)
  const profiles: ContextRecord[] = []
  const relations: ContextRelation[] = []
  const existingProfileIds = new Set<string>()

  SUBJECT_RULES.filter((rule) => rule.pattern.test(text)).forEach((rule) => {
    const existing = existingRecords.find((record) => (
      record.form === 'profile' && record.title.toLocaleLowerCase() === rule.title.toLocaleLowerCase()
    ))
    const profileId = existing?.id ?? `profile-${capture.userId}-${rule.key}`
    if (existing) existingProfileIds.add(profileId)

    profiles.push(existing ? {
      ...existing,
      sourceRefs: appendUniqueRef(existing.sourceRefs, sourceRef),
      updatedAt: now,
    } : {
      schemaVersion: CORE_SCHEMA_VERSION,
      id: profileId,
      userId: capture.userId,
      form: 'profile',
      kind: rule.kind,
      status: 'active',
      confirmation: 'inferred',
      title: rule.title,
      facts: [],
      memberRefs: [],
      sourceRefs: [sourceRef],
      relatedRefs: [],
      derivation: derivationFor(sourceRef, 0.94),
      createdAt: now,
      updatedAt: now,
    })

    const relationId = `relation-${capture.id}-${rule.key}`
    relations.push({
      schemaVersion: CORE_SCHEMA_VERSION,
      id: relationId,
      userId: capture.userId,
      status: 'active',
      confirmation: 'inferred',
      relation: rule.relation,
      fromRef: { kind: 'context', id: primaryContext.id },
      toRef: { kind: 'context', id: profileId },
      evidenceRefs: [sourceRef],
      derivation: derivationFor(sourceRef, 0.92),
      createdAt: now,
      updatedAt: now,
    })
  })

  return { profiles, relations, existingProfileIds }
}

const buildAction = (capture: Capture, now: string, id = `action-${capture.id}`): Action => {
  const sourceRef = captureRef(capture)
  const text = captureText(capture)

  return {
    schemaVersion: CORE_SCHEMA_VERSION,
    id,
    userId: capture.userId,
    status: 'ready',
    title: normalizeActionTitle(text),
    priority: 'medium',
    order: 0,
    contextRefs: [],
    sourceRefs: [sourceRef],
    idempotencyKey: `${capture.id}:action`,
    createdAt: now,
    updatedAt: now,
  }
}

export const buildCaptureAutomation = (
  capture: Capture,
  now = new Date().toISOString(),
  existingContextRecords: ContextRecord[] = [],
): CaptureAutomationWrite => {
  const sourceRef = captureRef(capture)
  const text = captureText(capture)
  const actionCandidate = looksLikeAction(text)
  const needsReview = actionCandidate && UNCERTAIN_LANGUAGE.test(text)

  if (needsReview) {
    const reviewId = `review-${capture.id}`
    const receiptId = `receipt-${capture.id}`
    const reviewItem: ReviewItem = {
      schemaVersion: CORE_SCHEMA_VERSION,
      id: reviewId,
      userId: capture.userId,
      type: 'interpretation',
      status: 'pending',
      title: clip(text),
      promptKey: 'core.review.uncertainActionPrompt',
      subjectRefs: [sourceRef],
      evidenceRefs: [sourceRef],
      recommendedChoiceId: 'keep_context',
      choices: [
        { id: 'create_action', labelKey: 'core.review.choices.createAction', operation: 'apply' },
        { id: 'keep_context', labelKey: 'core.review.choices.keepContext', operation: 'confirm' },
        { id: 'dismiss', labelKey: 'core.review.choices.dismiss', operation: 'dismiss' },
      ],
      receiptId,
      createdAt: now,
      updatedAt: now,
    }
    const receipt: AutomationReceipt = {
      schemaVersion: CORE_SCHEMA_VERSION,
      id: receiptId,
      userId: capture.userId,
      status: 'needs_review',
      operation: 'interpret_capture',
      summary: 'Held an uncertain action for review.',
      reason: 'The capture contains both action language and uncertainty.',
      inputRefs: [sourceRef],
      outputRefs: [{ kind: 'review', id: reviewId }],
      changes: [{ operation: 'create', targetRef: { kind: 'review', id: reviewId } }],
      derivation: derivationFor(sourceRef, 0.58),
      idempotencyKey: `${capture.id}:interpret`,
      reversible: true,
      reviewItemId: reviewId,
      createdAt: now,
      updatedAt: now,
    }

    return {
      expectedCaptureUpdatedAt: capture.updatedAt,
      capture: {
        ...capture,
        status: 'needs_review',
        processingVersion: PROCESSING_VERSION,
        outputRefs: [{ kind: 'review', id: reviewId }],
        processedAt: now,
        updatedAt: now,
      },
      reviewItems: [reviewItem],
      receipt,
    }
  }

  if (actionCandidate) {
    const action = buildAction(capture, now)
    const receipt: AutomationReceipt = {
      schemaVersion: CORE_SCHEMA_VERSION,
      id: `receipt-${capture.id}`,
      userId: capture.userId,
      status: 'applied',
      operation: 'create_action',
      summary: 'Created a ready action from explicit action language.',
      reason: 'The capture starts with a direct action or task phrase.',
      inputRefs: [sourceRef],
      outputRefs: [{ kind: 'action', id: action.id }],
      changes: [{ operation: 'create', targetRef: { kind: 'action', id: action.id } }],
      derivation: derivationFor(sourceRef, 0.95),
      idempotencyKey: `${capture.id}:interpret`,
      reversible: true,
      createdAt: now,
      updatedAt: now,
    }

    return {
      expectedCaptureUpdatedAt: capture.updatedAt,
      capture: {
        ...capture,
        status: 'processed',
        processingVersion: PROCESSING_VERSION,
        outputRefs: [{ kind: 'action', id: action.id }],
        processedAt: now,
        updatedAt: now,
      },
      actions: [action],
      receipt,
    }
  }

  const contextRecord = buildContextRecord(capture, now)
  const { profiles, relations, existingProfileIds } = buildSubjectContext(
    capture,
    contextRecord,
    existingContextRecords,
    now,
  )
  const contextRecords = [contextRecord, ...profiles]
  const contextRefs: CoreReference[] = contextRecords.map((record) => ({ kind: 'context', id: record.id }))
  const relationRefs: CoreReference[] = relations.map((relation) => ({ kind: 'context_relation', id: relation.id }))
  const receipt: AutomationReceipt = {
    schemaVersion: CORE_SCHEMA_VERSION,
    id: `receipt-${capture.id}`,
    userId: capture.userId,
    status: 'applied',
    operation: 'retain_context',
    summary: profiles.length
      ? `Retained the capture and connected ${profiles.map((profile) => profile.title).join(', ')}.`
      : 'Retained the capture as personal context.',
    reason: 'No consequential action was inferred from the capture.',
    inputRefs: [sourceRef],
    outputRefs: [...contextRefs, ...relationRefs],
    changes: [
      { operation: 'create', targetRef: { kind: 'context', id: contextRecord.id } },
      ...profiles.map((profile) => ({
        operation: existingProfileIds.has(profile.id) ? 'update' as const : 'create' as const,
        targetRef: { kind: 'context' as const, id: profile.id },
      })),
      ...relations.map((relation) => ({
        operation: 'link' as const,
        targetRef: { kind: 'context_relation' as const, id: relation.id },
      })),
    ],
    derivation: derivationFor(sourceRef, 0.86),
    idempotencyKey: `${capture.id}:interpret`,
    reversible: true,
    createdAt: now,
    updatedAt: now,
  }

  return {
    expectedCaptureUpdatedAt: capture.updatedAt,
    existingContextRecordIds: [...existingProfileIds],
    capture: {
      ...capture,
      status: 'processed',
      processingVersion: PROCESSING_VERSION,
      outputRefs: [...contextRefs, ...relationRefs],
      processedAt: now,
      updatedAt: now,
    },
    contextRecords,
    contextRelations: relations,
    receipt,
  }
}

const saveAndOrganizeCapture = async (
  initialCapture: Capture,
  enhancedInterpretation: boolean,
): Promise<CoreCaptureOutcome> => {
  const savedCapture = await coreRepositories.captures.create(initialCapture)

  try {
    const existingContextRecords = await coreRepositories.contextRecords.listForUser(savedCapture.userId, 500)
    const deterministic = buildCaptureAutomation(savedCapture, new Date().toISOString(), existingContextRecords)
    let automation = deterministic
    if (enhancedInterpretation && shouldEscalateCaptureInterpretation(savedCapture, deterministic)) {
      automation = await attemptModelInterpretation(savedCapture, deterministic, existingContextRecords)
    }
    await applyCaptureAutomation(automation)

    return {
      capture: automation.capture,
      contextRecord: automation.contextRecords?.[0],
      contextRecords: automation.contextRecords ?? [],
      contextRelations: automation.contextRelations ?? [],
      action: automation.actions?.[0],
      reviewItem: automation.reviewItems?.[0],
      receipt: automation.receipt,
      organizationState: automation.reviewItems?.length ? 'review' : 'organized',
    }
  } catch (error) {
    const failedAt = new Date().toISOString()
    const failedCapture = await coreRepositories.captures.update({
      ...savedCapture,
      status: 'failed',
      errorCode: 'capture_processing_failed',
      updatedAt: failedAt,
    })
    const sourceRef = captureRef(failedCapture)
    const failedReceipt = await coreRepositories.automationReceipts.create({
      schemaVersion: CORE_SCHEMA_VERSION,
      id: `receipt-failed-${failedCapture.id}`,
      userId: savedCapture.userId,
      status: 'failed',
      operation: 'interpret_capture',
      summary: 'Saved the capture without organizing it.',
      reason: error instanceof Error ? error.message : 'Capture processing failed.',
      inputRefs: [sourceRef],
      outputRefs: [],
      changes: [],
      derivation: derivationFor(sourceRef, 1),
      idempotencyKey: `${failedCapture.id}:interpret-failed`,
      reversible: false,
      createdAt: failedAt,
      updatedAt: failedAt,
    })

    return {
      capture: failedCapture,
      contextRecords: [],
      contextRelations: [],
      receipt: failedReceipt,
      organizationState: 'failed',
    }
  }
}

const initialCapture = (
  captureId: string,
  userId: string,
  inputs: CaptureInput[],
  primaryText: string,
): Capture => {
  const now = new Date().toISOString()
  return {
    schemaVersion: CORE_SCHEMA_VERSION,
    id: captureId,
    userId,
    status: 'saved',
    inputs,
    primaryText,
    idempotencyKey: `${captureId}:save`,
    outputRefs: [],
    createdAt: now,
    updatedAt: now,
  }
}

export const coreCaptureService = {
  async loadToday(userId: string): Promise<CoreTodayData> {
    const [captures, contextRecords, contextRelations, actions, reviewItems, receipts] = await Promise.all([
      coreRepositories.captures.listForUser(userId, 30),
      coreRepositories.contextRecords.listForUser(userId, 30),
      coreRepositories.contextRelations.listForUser(userId, 100),
      coreRepositories.actions.listForUser(userId, 30),
      coreRepositories.reviewItems.listForUser(userId, 30),
      coreRepositories.automationReceipts.listForUser(userId, 30),
    ])

    return { captures, contextRecords, contextRelations, actions, reviewItems, receipts }
  },

  async captureText(
    userId: string,
    text: string,
    entryMethod: CaptureEntryMethod = 'keyboard',
    enhancedInterpretation = true,
  ): Promise<CoreCaptureOutcome> {
    const primaryText = text.trim()
    if (!primaryText) throw new Error('Capture text cannot be empty.')

    const now = new Date().toISOString()
    const captureId = createId('capture')
    const initialCapture: Capture = {
      schemaVersion: CORE_SCHEMA_VERSION,
      id: captureId,
      userId,
      status: 'saved',
      inputs: [{ id: createId('input'), kind: 'text', content: primaryText, entryMethod }],
      primaryText,
      idempotencyKey: `${captureId}:save`,
      outputRefs: [],
      createdAt: now,
      updatedAt: now,
    }
    const savedCapture = await coreRepositories.captures.create(initialCapture)

    try {
      const existingContextRecords = await coreRepositories.contextRecords.listForUser(userId, 500)
      const deterministic = buildCaptureAutomation(savedCapture, new Date().toISOString(), existingContextRecords)
      let automation = deterministic
      if (enhancedInterpretation && shouldEscalateCaptureInterpretation(savedCapture, deterministic)) {
        automation = await attemptModelInterpretation(savedCapture, deterministic, existingContextRecords)
      }
      await applyCaptureAutomation(automation)

      return {
        capture: automation.capture,
        contextRecord: automation.contextRecords?.[0],
        contextRecords: automation.contextRecords ?? [],
        contextRelations: automation.contextRelations ?? [],
        action: automation.actions?.[0],
        reviewItem: automation.reviewItems?.[0],
        receipt: automation.receipt,
        organizationState: automation.reviewItems?.length ? 'review' : 'organized',
      }
    } catch (error) {
      const failedAt = new Date().toISOString()
      const failedCapture = await coreRepositories.captures.update({
        ...savedCapture,
        status: 'failed',
        errorCode: 'capture_processing_failed',
        updatedAt: failedAt,
      })
      const sourceRef = captureRef(failedCapture)
      const failedReceipt = await coreRepositories.automationReceipts.create({
        schemaVersion: CORE_SCHEMA_VERSION,
        id: `receipt-failed-${failedCapture.id}`,
        userId,
        status: 'failed',
        operation: 'interpret_capture',
        summary: 'Saved the capture without organizing it.',
        reason: error instanceof Error ? error.message : 'Capture processing failed.',
        inputRefs: [sourceRef],
        outputRefs: [],
        changes: [],
        derivation: derivationFor(sourceRef, 1),
        idempotencyKey: `${failedCapture.id}:interpret-failed`,
        reversible: false,
        createdAt: failedAt,
        updatedAt: failedAt,
      })

      return {
        capture: failedCapture,
        contextRecords: [],
        contextRelations: [],
        receipt: failedReceipt,
        organizationState: 'failed',
      }
    }
  },

  async captureImage(
    userId: string,
    note: string,
    image: NormalizedImage,
    entryMethod: CaptureEntryMethod = 'keyboard',
    enhancedInterpretation = true,
  ): Promise<CoreCaptureOutcome> {
    const captureId = createId('capture')
    const primaryText = note.trim() || 'Image attachment'
    const uploaded = await attachmentStorageService.uploadCaptureImage(userId, captureId, image)
    const inputs: CaptureInput[] = [
      ...(note.trim() ? [{
        id: createId('input'),
        kind: 'text' as const,
        content: note.trim(),
        entryMethod,
      }] : []),
      {
        id: createId('input'),
        kind: 'image',
        storagePath: uploaded.storagePath,
        mimeType: uploaded.mimeType,
        width: uploaded.width,
        height: uploaded.height,
        byteSize: uploaded.byteSize,
        hash: uploaded.hash,
      },
    ]

    try {
      return await saveAndOrganizeCapture(
        initialCapture(captureId, userId, inputs, primaryText),
        enhancedInterpretation,
      )
    } catch (error) {
      await attachmentStorageService.deleteOwnedImage(userId, uploaded.storagePath).catch(() => undefined)
      throw error
    }
  },

  async captureLink(
    userId: string,
    rawUrl: string,
    note: string,
    entryMethod: CaptureEntryMethod = 'keyboard',
    enhancedInterpretation = true,
  ): Promise<CoreCaptureOutcome> {
    const url = normalizeCaptureLink(rawUrl)
    const trimmedNote = note.trim()
    const captureId = createId('capture')
    const inputs: CaptureInput[] = [
      ...(trimmedNote ? [{
        id: createId('input'),
        kind: 'text' as const,
        content: trimmedNote,
        entryMethod,
      }] : []),
      {
        id: createId('input'),
        kind: 'link',
        url,
      },
    ]

    return saveAndOrganizeCapture(
      initialCapture(captureId, userId, inputs, trimmedNote || url),
      enhancedInterpretation,
    )
  },

  async retryCapture(captureId: string, enhancedInterpretation = true): Promise<CoreCaptureOutcome> {
    const capture = await coreRepositories.captures.get(captureId)
    if (!capture) throw new Error('Capture was not found.')
    if (capture.status !== 'failed') throw new Error('Only failed captures can be retried.')

    const existingContextRecords = await coreRepositories.contextRecords.listForUser(capture.userId, 500)
    const retriedCapture: Capture = {
      ...capture,
      status: 'saved',
      errorCode: undefined,
      updatedAt: new Date().toISOString(),
    }
    const deterministic = buildCaptureAutomation(retriedCapture, new Date().toISOString(), existingContextRecords)
    let automation = deterministic
    if (enhancedInterpretation && shouldEscalateCaptureInterpretation(retriedCapture, deterministic)) {
      automation = await attemptModelInterpretation(retriedCapture, deterministic, existingContextRecords)
    }
    automation.expectedCaptureUpdatedAt = capture.updatedAt
    await applyCaptureAutomation(automation)

    return {
      capture: automation.capture,
      contextRecord: automation.contextRecords?.[0],
      contextRecords: automation.contextRecords ?? [],
      contextRelations: automation.contextRelations ?? [],
      action: automation.actions?.[0],
      reviewItem: automation.reviewItems?.[0],
      receipt: automation.receipt,
      organizationState: automation.reviewItems?.length ? 'review' : 'organized',
    }
  },

  async completeAction(actionId: string): Promise<Action> {
    const action = await coreRepositories.actions.get(actionId)
    if (!action) throw new Error('Action was not found.')
    if (action.status === 'completed') return action

    const now = new Date().toISOString()
    return coreRepositories.actions.update({
      ...action,
      status: 'completed',
      completedAt: now,
      updatedAt: now,
    })
  },

  async resolveReview(reviewId: string, choiceId: string): Promise<{ applied: boolean }> {
    const reviewItem = await coreRepositories.reviewItems.get(reviewId)
    if (!reviewItem) throw new Error('Review item was not found.')
    if (reviewItem.status !== 'pending') return { applied: false }
    if (!reviewItem.choices.some((choice) => choice.id === choiceId)) {
      throw new Error('Review choice is not available.')
    }

    const captureId = reviewItem.subjectRefs.find((reference) => reference.kind === 'capture')?.id
    if (!captureId || !reviewItem.receiptId) {
      throw new Error('Review item is missing its capture or receipt reference.')
    }

    const [capture, receipt] = await Promise.all([
      coreRepositories.captures.get(captureId),
      coreRepositories.automationReceipts.get(reviewItem.receiptId),
    ])
    if (!capture || !receipt) throw new Error('Review context was not found.')

    const now = new Date().toISOString()
    const retainedContextRef = choiceId === 'keep_context'
      ? capture.outputRefs.find((reference) => (
          reference.kind === 'context' && reference.id === `context-${capture.id}`
        ))
      : undefined
    const retainedContext = retainedContextRef
      ? await coreRepositories.contextRecords.get(retainedContextRef.id)
      : null
    if (retainedContextRef && !retainedContext) {
      throw new Error('The context retained for this review was not found.')
    }
    const outputRef: CoreReference | undefined = choiceId === 'create_action'
      ? { kind: 'action', id: `action-${capture.id}` }
      : choiceId === 'keep_context'
        ? retainedContextRef ?? { kind: 'context', id: `context-${capture.id}` }
        : undefined
    const action = choiceId === 'create_action' ? buildAction(capture, now, outputRef?.id) : undefined
    const contextRecord = choiceId === 'keep_context'
      ? retainedContext
        ? { ...retainedContext, confirmation: 'confirmed' as const, updatedAt: now }
        : buildContextRecord(capture, now, outputRef?.id, 'confirmed')
      : undefined
    const dismissed = choiceId === 'dismiss'

    return applyReviewResolution({
      reviewItem: {
        ...reviewItem,
        status: dismissed ? 'dismissed' : 'resolved',
        resolvedChoiceId: choiceId,
        resolvedAt: now,
        updatedAt: now,
      },
      expectedReviewUpdatedAt: reviewItem.updatedAt,
      capture: {
        ...capture,
        status: 'processed',
        outputRefs: outputRef ? appendUniqueRef(capture.outputRefs, outputRef) : capture.outputRefs,
        updatedAt: now,
      },
      expectedCaptureUpdatedAt: capture.updatedAt,
      receipt: {
        ...receipt,
        status: dismissed ? 'reverted' : 'applied',
        outputRefs: outputRef ? appendUniqueRef(receipt.outputRefs, outputRef) : receipt.outputRefs,
        changes: outputRef
          ? [...receipt.changes, { operation: retainedContext ? 'update' : 'create', targetRef: outputRef }]
          : receipt.changes,
        updatedAt: now,
      },
      expectedReceiptUpdatedAt: receipt.updatedAt,
      action,
      contextRecord,
      expectedContextUpdatedAt: retainedContext?.updatedAt,
    })
  },
}
